import React, { useState } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Switch,
  IconButton,
  Tooltip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import LockIcon from '@material-ui/icons/Lock'
import PaletteIcon from '@material-ui/icons/Palette'
import TuneIcon from '@material-ui/icons/Tune'
import PersonPinIcon from '@material-ui/icons/PersonPin'
import EDITOR_COMPONENTS from './editors'
import ThemeEditor from './editors/ThemeEditor'
import VariablesEditor from './editors/VariablesEditor'
import ClientSiteEditor from './editors/ClientSiteEditor'

const MODULE_REGISTRY = [
  { id: 'cover', label: 'Cover Page', locked: true },
  { id: 'executiveSummary', label: 'Executive Summary', locked: false },
  { id: 'intro', label: 'Intro Letter', locked: false },
  { id: 'about', label: 'About Us', locked: false },
  { id: 'scope', label: 'Scope of Services', locked: false },
  { id: 'startup', label: 'Startup Plan (First 30 Days)', locked: false },
  { id: 'serviceArea', label: 'Service Area', locked: false },
  { id: 'project', label: 'Project Details', locked: false },
  { id: 'notes', label: 'Notes & Details', locked: false },
  { id: 'terms', label: 'Terms & Acceptance', locked: false }
]

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0
  },
  accordion: {
    '&:before': { display: 'none' },
    boxShadow: 'none',
    borderBottom: '1px solid #eee'
  },
  themeAccordion: {
    '&:before': { display: 'none' },
    boxShadow: 'none',
    borderBottom: '2px solid #e0e0e0',
    background: '#fafafa'
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  moduleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  details: {
    display: 'block',
    padding: '8px 16px 16px'
  },
  reorderBtn: {
    padding: 4
  },
  disabledLabel: {
    opacity: 0.5
  },
  paletteIcon: {
    marginRight: 8,
    fontSize: 18,
    color: '#888'
  }
}))

const AccordionEditor = ({
  proposal,
  getModuleData,
  updateModuleOverride,
  toggleModule,
  reorderModules,
  updateCrmSnapshot,
  updateCrmSnapshots,
  theme,
  onThemeChange,
  variableDefinitions,
  resolvedVariables,
  onVariableChange,
  onResetVariables,
  proposalVariables,
  url,
  updateJobsite,
  updateModuleOverrides
}) => {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false)

  if (!proposal) return null

  const sortedModules = [...(proposal.modules || [])].sort((a, b) => a.order - b.order)
  const crmSnapshot = proposal.crmSnapshot || {}

  const handleAccordionChange = (moduleId) => (event, isExpanded) => {
    setExpanded(isExpanded ? moduleId : false)
  }

  return (
    <div className={classes.root}>
      {/* Client & Site Selector */}
      <Accordion
        className={classes.themeAccordion}
        expanded={expanded === '__clientSite'}
        onChange={handleAccordionChange('__clientSite')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <div className={classes.moduleLabel}>
            <PersonPinIcon className={classes.paletteIcon} />
            <Typography variant="subtitle2">Client &amp; Site</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <ClientSiteEditor
            proposal={proposal}
            updateCrmSnapshot={updateCrmSnapshot}
            updateCrmSnapshots={updateCrmSnapshots}
            url={url}
            updateJobsite={updateJobsite}
            updateModuleOverrides={updateModuleOverrides}
          />
        </AccordionDetails>
      </Accordion>

      {/* Theme / Brand Editor */}
      <Accordion
        className={classes.themeAccordion}
        expanded={expanded === '__theme'}
        onChange={handleAccordionChange('__theme')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <div className={classes.moduleLabel}>
            <PaletteIcon className={classes.paletteIcon} />
            <Typography variant="subtitle2">Brand &amp; Theme</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <ThemeEditor
            theme={theme}
            onChange={onThemeChange}
          />
        </AccordionDetails>
      </Accordion>

      {/* Variables Editor */}
      <Accordion
        className={classes.themeAccordion}
        expanded={expanded === '__variables'}
        onChange={handleAccordionChange('__variables')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <div className={classes.moduleLabel}>
            <TuneIcon className={classes.paletteIcon} />
            <Typography variant="subtitle2">Mtc Proposal Variables</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <VariablesEditor
            variableDefinitions={variableDefinitions}
            resolvedVariables={resolvedVariables}
            onVariableChange={onVariableChange}
            onResetVariables={onResetVariables}
            proposalVariables={proposalVariables}
          />
        </AccordionDetails>
      </Accordion>

      {/* Module Editors */}
      {sortedModules.map((mod, index) => {
        const regEntry = MODULE_REGISTRY.find(r => r.id === mod.moduleId)
        const Editor = EDITOR_COMPONENTS[mod.moduleId]
        if (!Editor) return null
        const data = getModuleData(mod.moduleId)

        return (
          <Accordion
            key={mod.moduleId}
            className={classes.accordion}
            expanded={expanded === mod.moduleId}
            onChange={handleAccordionChange(mod.moduleId)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <div className={classes.summaryContent}>
                <div className={classes.moduleLabel}>
                  <Typography
                    variant="subtitle2"
                    className={!mod.enabled ? classes.disabledLabel : ''}
                  >
                    {regEntry?.label || mod.moduleId}
                  </Typography>
                  {regEntry?.locked && (
                    <Tooltip title="Always included">
                      <LockIcon style={{ fontSize: 14, color: '#aaa' }} />
                    </Tooltip>
                  )}
                </div>
                <div className={classes.controls} onClick={(e) => e.stopPropagation()}>
                  {index > 0 && (
                    <IconButton
                      className={classes.reorderBtn}
                      size="small"
                      onClick={(e) => { e.stopPropagation(); reorderModules(index, index - 1) }}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  )}
                  {index < sortedModules.length - 1 && (
                    <IconButton
                      className={classes.reorderBtn}
                      size="small"
                      onClick={(e) => { e.stopPropagation(); reorderModules(index, index + 1) }}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  )}
                  {!regEntry?.locked && (
                    <Switch
                      size="small"
                      checked={mod.enabled}
                      onChange={(e) => { e.stopPropagation(); toggleModule(mod.moduleId) }}
                      color="primary"
                    />
                  )}
                </div>
              </div>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Editor
                data={data}
                crmSnapshot={crmSnapshot}
                theme={theme}
                variables={resolvedVariables}
                onChange={(field, value) => updateModuleOverride(mod.moduleId, field, value)}
                onCrmChange={updateCrmSnapshot}
              />
            </AccordionDetails>
          </Accordion>
        )
      })}
    </div>
  )
}

export default AccordionEditor
